/**
 * Room occupancy and booking models for the processor dashboard's Room Usage page.
 * Field names match the processor's OccupancyDtos (proAV.Net.Core.DeviceSupport.Occupancy).
 */

export type OccupancyState = 'Unknown' | 'Vacant' | 'Occupied';
export type OccupancyFeature = 'Presence' | 'PeopleCount';
export type BookingAvailability = 'Unknown' | 'Free' | 'BookedSoon' | 'Booked';
export type BookingFeature = 'ReadCalendar' | 'AdHocBook' | 'Extend' | 'End';

export interface OccupancyReading {
    state: OccupancyState;
    peopleCount?: number | null;
    changedUtc?: string | null;
}

export interface OccupancySensor {
    id: number;
    uniqueId?: string | null;
    name: string;
    manufacturerName?: string | null;
    modelName?: string | null;
    online: boolean;
    features: OccupancyFeature[];
    capacity?: number | null;
    roomId?: number | null;
    roomName?: string | null;
    reading: OccupancyReading;
}

/** A meeting. Private meetings arrive with a masked title and no organizer. */
export interface RoomBooking {
    id: string;
    title: string;
    organizer?: string | null;
    startUtc: string;
    endUtc: string;
    isPrivate: boolean;
    meetingPlatform?: string | null;
    canJoin: boolean;
}

export interface BookingStatus {
    availability: BookingAvailability;
    current?: RoomBooking | null;
    next?: RoomBooking | null;
    freeUntilUtc?: string | null;
}

export interface BookingSource {
    id: number;
    uniqueId?: string | null;
    name: string;
    manufacturerName?: string | null;
    modelName?: string | null;
    online: boolean;
    features: BookingFeature[];
    roomId?: number | null;
    roomName?: string | null;
    status: BookingStatus;
    bookings: RoomBooking[];
}

/** How light a space is, as the sensor classifies it against its own thresholds (proAV.Net.Core `LightLevel`). */
export type LightLevel = 'Dark' | 'Twilight' | 'Bright';

/** Font Awesome icon for a light level: moon, cloud-sun or sun (sun when only lux is known). */
export function lightLevelIcon(level: LightLevel | null | undefined): string {
    return level === 'Dark' ? 'fa fa-moon' : level === 'Twilight' ? 'fa fa-cloud-sun' : 'fa fa-sun';
}

/** Environment values (IEnvironmentSensor / RoomBase.EnvironmentReading). Null when not measured. */
export interface EnvironmentReading {
    temperatureC?: number | null;
    humidityPercent?: number | null;
    illuminanceLux?: number | null;
    /** Some sensors (e.g. Shelly Presence Gen4) classify the light, with or without lux. */
    lightLevel?: LightLevel | null;
    updatedUtc?: string | null;
}

export type EnvironmentFeature = 'Temperature' | 'Humidity' | 'Illuminance';

export interface EnvironmentSensor {
    id: number;
    uniqueId?: string | null;
    name: string;
    manufacturer?: string | null;
    model?: string | null;
    online: boolean;
    features: EnvironmentFeature[];
    roomId?: number | null;
    roomName?: string | null;
    reading: EnvironmentReading;
}

export interface RoomUsage {
    roomId: number;
    roomName: string;
    hasOccupancy: boolean;
    occupancy: OccupancyReading;
    capacity?: number | null;
    hasBookingFacility: boolean;
    sensors: OccupancySensor[];
    bookingSource?: BookingSource | null;
    /** The room has environment sensors. Optional for processors running an older framework. */
    hasEnvironment?: boolean;
    /** The room's combined environment (averaged and rounded by the processor). */
    environment?: EnvironmentReading | null;
    environmentSensors?: EnvironmentSensor[];
}

/** True when the reading has at least one value. */
export function hasEnvironmentValue(reading: EnvironmentReading | null | undefined): reading is EnvironmentReading {
    return !!reading && ([reading.temperatureC, reading.humidityPercent, reading.illuminanceLux].some((v) => typeof v === 'number') || !!reading.lightLevel);
}

/** `DeviceEvent:device.{id}.OccupancyChanged` payload. */
export interface OccupancyChangedEvent {
    deviceId: number;
    deviceName: string;
    roomId?: number | null;
    reading: OccupancyReading;
}

/** `DeviceEvent:device.{id}.BookingsChanged` payload. Carries no meeting details by design. */
export interface BookingsChangedEvent {
    deviceId: number;
    roomId?: number | null;
    availability: BookingAvailability;
    bookingsChanged: boolean;
}

/** `DeviceEvent:device.{id}.EnvironmentChanged` payload. */
export interface EnvironmentChangedEvent {
    deviceId: number;
    deviceName: string;
    roomId?: number | null;
    reading: EnvironmentReading;
}

export type RoomUsageUpdate = { kind: 'occupancy'; deviceId: number; event: OccupancyChangedEvent } | { kind: 'bookings'; deviceId: number; event: BookingsChangedEvent } | { kind: 'environment'; deviceId: number; event: EnvironmentChangedEvent };

const STATE_RANK: Record<OccupancyState, number> = { Unknown: 0, Vacant: 1, Occupied: 2 };

/**
 * Combine sensor readings the way the processor does (RoomBase.OccupancyReading): occupied wins over vacant,
 * vacant over unknown; the people count is the highest reported.
 */
export function combineReadings(readings: OccupancyReading[]): OccupancyReading {
    if (readings.length === 0) return { state: 'Unknown', peopleCount: null, changedUtc: null };
    const state = readings.reduce<OccupancyState>((s, r) => (STATE_RANK[r.state] > STATE_RANK[s] ? r.state : s), 'Unknown');
    const counts = readings.map((r) => r.peopleCount).filter((c): c is number => typeof c === 'number');
    const times = readings
        .map((r) => r.changedUtc)
        .filter((t): t is string => !!t)
        .sort();
    return { state, peopleCount: counts.length ? Math.max(...counts) : null, changedUtc: times.at(-1) ?? null };
}

/**
 * Apply a live sensor reading to the room it belongs to and recompute the room's combined occupancy.
 * Returns the same array when no room has that sensor.
 */
export function applyOccupancyEvent(rooms: RoomUsage[], event: OccupancyChangedEvent): RoomUsage[] {
    let changed = false;
    const next = rooms.map((room) => {
        if (!room.sensors.some((s) => s.id === event.deviceId)) return room;
        changed = true;
        const sensors = room.sensors.map((s) => (s.id === event.deviceId ? { ...s, reading: event.reading } : s));
        return { ...room, sensors, occupancy: combineReadings(sensors.map((s) => s.reading)) };
    });
    return changed ? next : rooms;
}

/** Fraction (0-1) of a meeting that has elapsed at `nowMs`. */
export function bookingProgress(booking: RoomBooking, nowMs: number): number {
    const start = Date.parse(booking.startUtc);
    const end = Date.parse(booking.endUtc);
    if (!(end > start)) return 0;
    return Math.min(1, Math.max(0, (nowMs - start) / (end - start)));
}

/** Meetings that have not ended yet, in start order. */
export function remainingBookings(bookings: RoomBooking[], nowMs: number): RoomBooking[] {
    return bookings.filter((b) => Date.parse(b.endUtc) > nowMs).sort((a, b) => Date.parse(a.startUtc) - Date.parse(b.startUtc));
}
